import { useTranslation } from "react-i18next";
import { type ThemePreference, useTheme } from "~/foundation/theme/useTheme";
import { cn } from "~/foundation/utils/cn";

const OPTIONS: Array<{ value: ThemePreference; label: string }> = [
  { value: "light", label: "Light" },
  { value: "dark", label: "Dark" },
  { value: "system", label: "System" },
];

interface MiniPalette {
  bar: string;
  side: string;
  body: string;
  line: string;
}

const LIGHT_MINI: MiniPalette = {
  bar: "#FFFFFF",
  side: "#EDF2F7",
  body: "#FFFFFF",
  line: "#DAE1E9",
};
const DARK_MINI: MiniPalette = {
  bar: "#08090C",
  side: "#08090C",
  body: "#111319",
  line: "#2A2E38",
};

function MiniShell({ palette }: { palette: MiniPalette }) {
  return (
    <div className="flex-1" style={{ background: palette.body }}>
      <div
        style={{ height: 8, background: palette.bar, borderBottom: `1px solid ${palette.line}` }}
      />
      <div className="flex h-[calc(100%-8px)]">
        <div style={{ width: 14, background: palette.side }} />
        <div className="flex-1" />
      </div>
    </div>
  );
}

/**
 * Miniature of the shell. These are the only literal hex values in a component, and they
 * are deliberate: a "Dark" card must show dark even while the app is in light.
 */
function Preview({ mode }: { mode: "light" | "dark" | "split" }) {
  return (
    <div aria-hidden className="flex h-16 w-full overflow-hidden rounded-sm border border-divider">
      <MiniShell palette={mode === "dark" ? DARK_MINI : LIGHT_MINI} />
      {mode === "split" ? <MiniShell palette={DARK_MINI} /> : null}
    </div>
  );
}

export function Appearance() {
  const { t } = useTranslation();
  const { preference, setPreference } = useTheme();

  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
      {OPTIONS.map((option) => (
        <button
          key={option.value}
          type="button"
          aria-pressed={preference === option.value}
          onClick={() => setPreference(option.value)}
          className={cn(
            "flex flex-col gap-2 rounded-md border p-2 text-start",
            preference === option.value
              ? "border-accent ring-1 ring-accent"
              : "border-divider hover:bg-hover-bg",
          )}
        >
          <Preview
            mode={option.value === "system" ? "split" : option.value === "dark" ? "dark" : "light"}
          />
          <span className="text-body text-text">{t(option.label)}</span>
        </button>
      ))}
    </div>
  );
}
